import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import i18n from "../../utils/i18n";
import ShiftValidator from "./ShiftValidator";
import Modal from "../Modal";
import ShiftMembersFilters from "./ShiftMembersFilters";
import ShiftMembersList from "./ShiftMembersList";
import { removeMember, toggleLeader } from "../../stores/member/memberActions";

const CONFIRMED_TAB = "CONFIRMED_TAB";
const SUGGESTED_TAB = "SUGGESTED_TAB";

const allSortByOptions = [
  {
    key: "sort_by_name",
    comparator(member1, member2) {
      return member1.name.localeCompare(member2.name);
    },
  },
  {
    key: "sort_by_shifts_count",
    comparator(member1, member2) {
      return member1.total_shift_count - member2.total_shift_count;
    },
  },
  {
    key: "sort_by_hours",
    comparator(member1, member2) {
      return member1.total_hour_count - member2.total_hour_count;
    },
  },
  {
    key: "sort_by_leader",
    comparator(member1, member2) {
      if (member1.leader) {
        return member2.leader ? 0 : -1;
      } else {
        return member2.leader ? 1 : 0;
      }
    },
  },
  {
    key: "sort_by_preferred",
    comparator(member1, member2) {
      const memberPreferrenceIndex = function (member) {
        return (
          (member.total_shift_count * member.total_hour_count) /
          (member.total_preferred_shift_count *
            member.total_preferred_hour_count)
        );
      };
      return (
        memberPreferrenceIndex(member1) - memberPreferrenceIndex(member2)
      );
    },
  },
];

const ShowMembersButton = ({ shift, memberTypeProperties, onAddMember }) => {
  const dispatch = useDispatch();
  const members_by_id = useSelector((state) => state.member.members_by_id);
  const shifts = useSelector((state) => state.member.shifts);

  const [showModal, setShowModal] = useState(false);
  const [tab, setTab] = useState(CONFIRMED_TAB);
  const [filterValue, setFilterValue] = useState("");
  const [sortValue, setSortValue] = useState("sort_by_name");
  const [sortDirection, setSortDirection] = useState("asc");

  const toggleTab = (tab) => {
    setTab(tab);
    setFilterValue("");
    setSortValue(tab === CONFIRMED_TAB ? "sort_by_name" : "sort_by_preferred");
  };

  const tabElementClassname = (currentTab, extraClass) => {
    let className = "";
    if (extraClass) className += extraClass + " ";
    if (tab === currentTab) className += "active";
    return className;
  };

  const handleAddMemberClick = (member) => {
    onAddMember(member);
  };

  const handleRemoveMemberClick = (member) => {
    dispatch(removeMember(shift.identifier, member));
  };

  const handleToggleLeaderClick = (member) => {
    dispatch(toggleLeader(shift.identifier, member.id));
  };

  const memberActionButtons = (member) => {
    const leaderButtonColor = member.leader ? "primary" : "white";
    return (
      <div>
        <button
          type="button"
          className={"btn btn-block btn-xs btn-" + leaderButtonColor}
          onClick={() => handleToggleLeaderClick(member)}
        >
          <i className="fa fa-star" aria-hidden="true"></i> {i18n.t("schedule.shifts.members.leader")}
        </button>
        <button
          type="button"
          className="btn btn-block btn-danger btn-xs"
          onClick={() => handleRemoveMemberClick(member)}
        >
          <i className="fa fa-times" aria-hidden="true"></i> {i18n.t("schedule.shifts.members.remove")}
        </button>
      </div>
    );
  };

  const suggestedMemberActionButtons = (member) => (
    <div>
      <button
        type="button"
        className="btn btn-block btn-white btn-xs"
        onClick={() => handleAddMemberClick(member)}
      >
        <i className="fa fa-plus" aria-hidden="true" /> {i18n.t("schedule.shifts.members.assign")}
      </button>
    </div>
  );

  const handleFilterValueChange = (e) => setFilterValue(e.target.value);
  const handleSortValueChange = (e) => setSortValue(e.target.value);
  const toggleSortDirection = () =>
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");

  // Members logic
  const shiftMembers = shift.members;
  const shiftMembersIds = shiftMembers.map((member) => member.id);
  const shiftSuggestedMembers = (shift.preferred_members || []).filter(
    (preferredMember) =>
      !shiftMembersIds.includes(preferredMember.id) &&
      ShiftValidator.validFor(shifts, shift, members_by_id[preferredMember.id])
  );

  let membersItems, actionButtons, sortOptions;
  if (tab === CONFIRMED_TAB) {
    membersItems = shiftMembers;
    actionButtons = memberActionButtons;
    sortOptions = [
      "sort_by_name",
      "sort_by_shifts_count",
      "sort_by_hours",
      "sort_by_leader",
    ];
  } else {
    membersItems = shiftSuggestedMembers;
    actionButtons = suggestedMemberActionButtons;
    sortOptions = [
      "sort_by_preferred",
      "sort_by_name",
      "sort_by_shifts_count",
      "sort_by_hours",
    ];
  }

  const sortingFn = allSortByOptions.find((option) => option.key === sortValue)
    .comparator;

  return (
    <div>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="btn btn-white btn-block btn-xs"
      >
        {i18n.t("schedule.shifts.show_members")}
      </button>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={i18n.t("schedule.shifts.members.modal_header")}
      >
        <div className="modal-body">
          <div className="tabs-container">
            <ul className="nav nav-tabs">
              <li className={tabElementClassname(CONFIRMED_TAB)}>
                <a onClick={() => toggleTab(CONFIRMED_TAB)}>
                  <span className="badge">{shiftMembers.length}</span> {i18n.t("schedule.shifts.members.tab_name.assigned")}
                </a>
              </li>
              <li className={tabElementClassname(SUGGESTED_TAB)}>
                <a onClick={() => toggleTab(SUGGESTED_TAB)}>
                  <span className="badge">{shiftSuggestedMembers.length}</span> {i18n.t("schedule.shifts.members.tab_name.suggested")}
                </a>
              </li>
            </ul>
            <div className="tab-content">
              <div className="active tab-pane">
                <div className="panel-body">
                  <ShiftMembersFilters
                    sortOptions={sortOptions}
                    sortValue={sortValue}
                    onSortValueChange={handleSortValueChange}
                    sortDirection={sortDirection}
                    onSortDirectionChange={toggleSortDirection}
                    filterValue={filterValue}
                    onFilterChange={handleFilterValueChange}
                  />
                  <ShiftMembersList
                    members={membersItems}
                    actionButtons={actionButtons}
                    filterValue={filterValue}
                    sortingFn={sortingFn}
                    sortDirection={sortDirection}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShowMembersButton;

